import { readSupabasePublicConfig } from "@dashboard-config"
import {
  type CookieMethodsServer,
  type CookieOptionsWithName,
  createChunks,
  createServerClient,
} from "@supabase/ssr"
import type { SupabaseClient } from "@supabase/supabase-js"

/**
 * `@tanstack/react-start/server` is imported LAZILY, inside the write path
 * only. It reaches node:async_hooks, and this module sits in the
 * client-reachable graph via `dashboardSession` -- a static import ships the
 * Node-only module to the browser, where the bundler shims AsyncLocalStorage
 * to undefined and hydration dies with "not a constructor". The build cannot
 * catch that (the shim resolves); only the browser can, and did.
 */
async function serverRuntime() {
  return import("@tanstack/react-start/server")
}

export type DashboardSupabaseClientConfig = {
  url: string
  publishableKey: string
}

export type DashboardSupabaseClientDeps = {
  config?: DashboardSupabaseClientConfig
  cookies?: CookieMethodsServer
}

export function createDashboardSupabaseServerClient(deps: DashboardSupabaseClientDeps = {}): SupabaseClient {
  const config = deps.config ?? requireDashboardSupabaseConfig()
  return createServerClient(config.url, config.publishableKey, {
    cookieOptions: dashboardSupabaseCookieOptions(),
    cookies: deps.cookies ?? tanStackCookieMethods(),
  })
}

export function dashboardSupabaseCookieOptions(): CookieOptionsWithName {
  return {
    httpOnly: true,
    path: "/",
    sameSite: "lax",
    secure: shouldUseSecureCookies(),
  }
}

function requireDashboardSupabaseConfig(): DashboardSupabaseClientConfig {
  const config = readSupabasePublicConfig()
  const publishableKey = process.env.SUPABASE_PUBLISHABLE_KEY?.trim()
  if (!config || !publishableKey) {
    throw new Error("Dashboard Supabase authentication is not configured.")
  }
  return { url: config.url, publishableKey }
}

/**
 * Cookie methods bound to an explicit Request: reads parse the request's own
 * Cookie header (no ambient context needed, so loaders and tests behave), and
 * writes go to the TanStack response context when one exists -- outside one
 * (unit tests) they are silently dropped, which is correct there: the test
 * asserts behaviour, not transport.
 */
export function requestCookieMethods(request: Request): CookieMethodsServer {
  return {
    getAll: () => {
      const header = request.headers.get("cookie")
      if (!header) return []
      const cookies: { name: string; value: string }[] = []
      for (const part of header.split(";")) {
        const [name, ...valueParts] = part.trim().split("=")
        if (!name) continue
        cookies.push({ name, value: safeDecodeCookieValue(valueParts.join("=")) })
      }
      return cookies
    },
    setAll: async (cookies) => {
      try {
        const { setCookie } = await serverRuntime()
        for (const { name, value, options } of slimSessionCookies(cookies)) {
          setCookie(name, value, options)
        }
      } catch {
        // No response context (unit tests, client navigation); nothing to attach to.
      }
    },
  }
}

/**
 * A cookie the server never wrote can hold anything, including percent
 * sequences that make `decodeURIComponent` THROW — and a getAll that throws
 * turns every request from that browser into a 500 until the person clears
 * their cookies. Undecodable values pass through raw instead: the SDK then
 * simply fails to read a session out of them, which is a clean 401.
 */
function safeDecodeCookieValue(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function tanStackCookieMethods(): CookieMethodsServer {
  return {
    getAll: async () => {
      const { getCookies } = await serverRuntime()
      return Object.entries(getCookies()).map(([name, value]) => ({ name, value: String(value ?? "") }))
    },
    setAll: async (cookies, headers) => {
      const { setCookie, setResponseHeader } = await serverRuntime()
      for (const { name, value, options } of slimSessionCookies(cookies)) {
        setCookie(name, value, options)
      }
      for (const [name, value] of Object.entries(headers ?? {})) setResponseHeader(name, value)
    },
  }
}

/** How many stale chunk suffixes to sweep when a session shrinks back to one cookie. */
const STALE_CHUNKS_TO_CLEAR = 5

/**
 * Strips the stored session down to what this server actually uses.
 *
 * The SDK serialises the WHOLE session into the cookie -- tokens plus the full
 * GoTrue user object, identities and copied OAuth profile included. For a
 * Google-linked account that is ~2KB of user data, which pushes the cookie past
 * MAX_CHUNK_SIZE and splits it into `.0`/`.1` chunks.
 *
 * None of that user data is ever read here: every session read validates with
 * `auth.getUser()` (a live call) and resolves the account from SQL -- the
 * roadmap's "Sessions, revised" design explicitly refuses to trust stored
 * claims. So the stored user is dead weight, and this rewrites the session
 * cookie on every write to keep only the tokens, the expiry bookkeeping, and a
 * user stub carrying the id. Refreshes arrive from the server with a full user
 * again and are slimmed again on their way out.
 *
 * Anything that fails to decode passes through untouched -- worst case is the
 * SDK's own chunked cookie, exactly what shipped before this existed. When the
 * slim value fits one cookie where chunks previously existed, the stale
 * `.0`/`.N` chunks are actively expired so browsers that hold the old pair are
 * swept on their next write.
 */
export function slimSessionCookies(
  cookiesToSet: { name: string; value: string; options?: Record<string, unknown> }[]
): { name: string; value: string; options?: Record<string, unknown> }[] {
  type Entry = (typeof cookiesToSet)[number]
  const sessionGroups = new Map<string, Entry[]>()
  const passthrough: Entry[] = []

  for (const entry of cookiesToSet) {
    // The session cookie only: `sb-<ref>-auth-token` and its numbered chunks.
    // The anchored suffix keeps the PKCE `-auth-token-code-verifier` cookie out.
    const match = /^(.*-auth-token)(?:\.\d+)?$/.exec(entry.name)
    if (match && entry.value) {
      const group = sessionGroups.get(match[1]) ?? []
      group.push(entry)
      sessionGroups.set(match[1], group)
    } else {
      passthrough.push(entry)
    }
  }

  const result = [...passthrough]
  for (const [baseName, group] of sessionGroups) {
    result.push(...slimOneSession(baseName, group))
  }
  return result
}

function slimOneSession(
  baseName: string,
  group: { name: string; value: string; options?: Record<string, unknown> }[]
): { name: string; value: string; options?: Record<string, unknown> }[] {
  const chunkIndex = (name: string) => {
    const suffix = /\.(\d+)$/.exec(name)
    return suffix ? Number(suffix[1]) : -1
  }
  const ordered = [...group].sort((a, b) => chunkIndex(a.name) - chunkIndex(b.name))
  const combined = ordered.map((entry) => entry.value).join("")
  const options = ordered[0].options

  let session: Record<string, unknown>
  try {
    const json = combined.startsWith("base64-")
      ? Buffer.from(combined.slice("base64-".length), "base64url").toString("utf-8")
      : combined
    session = JSON.parse(json) as Record<string, unknown>
  } catch {
    return group
  }
  const user = session.user
  if (!user || typeof user !== "object") return group

  const slimmed = {
    ...session,
    user: { id: (user as { id?: string }).id ?? null },
  }
  const encoded = `base64-${Buffer.from(JSON.stringify(slimmed), "utf-8").toString("base64url")}`

  const result: { name: string; value: string; options?: Record<string, unknown> }[] = []
  const chunks = createChunks(baseName, encoded)
  for (const chunk of chunks) result.push({ ...chunk, options })

  // Expire every chunk name this write no longer uses, plus the bare name when
  // the value is chunked -- whichever shape the browser held before, it ends
  // this response holding exactly one shape.
  const written = new Set(chunks.map((chunk) => chunk.name))
  const candidates = [baseName]
  for (let i = 0; i < STALE_CHUNKS_TO_CLEAR; i++) candidates.push(`${baseName}.${i}`)
  for (const name of candidates) {
    if (written.has(name)) continue
    result.push({ name, value: "", options: { ...options, maxAge: 0 } })
  }
  return result
}

function shouldUseSecureCookies() {
  const stage = process.env.PUBLIC_STAGE || process.env.VITE_PUBLIC_STAGE
  const domain = process.env.DOMAIN || ""
  if (domain.includes("localhost") || domain.includes("127.0.0.1")) return false
  return stage !== "local" && stage !== undefined
}
